import { defineMessages } from '@/i18n'

/**
 * Words used across many screens: navigation, shared buttons and states. Also the glossary the
 * feature catalogs follow, so a term reads the same everywhere.
 *
 * Domain terms the crews already use stay as they are in every language: Aufmaß, Bauleiter,
 * RTK, SUB / MIH (pricing), HA / POP / DP, ROM.
 *
 * Crnogorski and Bosanski are both ijekavian Latin and mostly identical; where usage differs
 * the catalogs follow each standard (e.g. week: cnr "nedjelja", bs "sedmica"; condition: cnr
 * "uslov", bs "uvjet"; history: cnr "istorija", bs "historija"). Srpski (ekavian) follows
 * Crnogorski with the ekavian forms: mjesto → mesto, prije → pre, nedjelja → nedelja,
 * dionica → deonica.
 */
export const commonMessages = defineMessages({
  en: {
    appName: 'Aufmass',
    madeBy: 'Made by SK Media d.o.o. & XPRO d.o.o. Slovenia',
    noProject: '(no project)',
    navDashboard: 'Dashboard',
    navRtk: 'RTK',
    navAufmass: 'Aufmaß',
    navProjects: 'Projects',
    navBonus: 'Bonus',
    navUsers: 'Users',
    navMobileUsers: 'Mobile users',
    navInventory: 'Inventory',
    navFleet: 'Fleet',
    navMyWork: 'My work',
    navPeople: 'People',
    navExecution: 'Execution',
    navBilling: 'Billing',
    navCrews: 'Crews',
    navChangelog: 'Change log',
    navGroupWork: 'Work',
    navGroupOps: 'Operations',
    navGroupAdmin: 'Admin',
    menu: 'Menu',
    pages: 'Pages',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    signOut: 'Sign out',
    language: 'Language',
    filters: 'Filters',
    filtersActive: { one: 'Filters, {count} active', other: 'Filters, {count} active' },
    reset: 'Reset',
    close: 'Close',
    closeNamed: 'Close {name}',
    showResults: 'Show results',
    search: 'Search',
    loading: 'Loading…',
    retry: 'Retry',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    all: 'All',
    none: 'None',
    yes: 'Yes',
    no: 'No',
    previousPage: 'Previous page',
    nextPage: 'Next page',
    rowsPerPage: 'Rows per page',
    rows: 'Rows',
    noData: 'No data.',
    couldNotLoad: 'Could not load {what}.',
    readOnly: 'Read-only',
    metres: 'Metres',
    hours: 'Hours',
    crewDays: { one: '{count} crew-day', few: '{count} crew-days', other: '{count} crew-days' },
    crews: { one: '{count} crew', other: '{count} crews' },
  },
  de: {
    appName: 'Aufmass',
    madeBy: 'Entwickelt von SK Media d.o.o. & XPRO d.o.o. Slovenia',
    noProject: '(kein Projekt)',
    navDashboard: 'Übersicht',
    navRtk: 'RTK',
    navAufmass: 'Aufmaß',
    navProjects: 'Projekte',
    navBonus: 'Bonus',
    navUsers: 'Benutzer',
    navMobileUsers: 'Mobile Nutzer',
    navInventory: 'Lager',
    navFleet: 'Fuhrpark',
    navMyWork: 'Aufgaben',
    navPeople: 'Personal',
    navExecution: 'Ausführung',
    navBilling: 'Abrechnung',
    navCrews: 'Kolonnen',
    navChangelog: 'Änderungsprotokoll',
    navGroupWork: 'Arbeit',
    navGroupOps: 'Betrieb',
    navGroupAdmin: 'Admin',
    menu: 'Menü',
    pages: 'Seiten',
    openMenu: 'Menü öffnen',
    closeMenu: 'Menü schließen',
    signOut: 'Abmelden',
    language: 'Sprache',
    filters: 'Filter',
    filtersActive: { one: 'Filter, {count} aktiv', other: 'Filter, {count} aktiv' },
    reset: 'Zurücksetzen',
    close: 'Schließen',
    closeNamed: '{name} schließen',
    showResults: 'Ergebnisse anzeigen',
    search: 'Suche',
    loading: 'Wird geladen…',
    retry: 'Erneut versuchen',
    save: 'Speichern',
    cancel: 'Abbrechen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    all: 'Alle',
    none: 'Keine',
    yes: 'Ja',
    no: 'Nein',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    rowsPerPage: 'Zeilen pro Seite',
    rows: 'Zeilen',
    noData: 'Keine Daten.',
    couldNotLoad: '{what} konnte nicht geladen werden.',
    readOnly: 'Nur Lesen',
    metres: 'Meter',
    hours: 'Stunden',
    crewDays: { one: '{count} Kolonnentag', other: '{count} Kolonnentage' },
    crews: { one: '{count} Kolonne', other: '{count} Kolonnen' },
  },
  cnr: {
    appName: 'Aufmass',
    madeBy: 'Izradili SK Media d.o.o. & XPRO d.o.o. Slovenia',
    noProject: '(bez projekta)',
    navDashboard: 'Pregled',
    navRtk: 'RTK',
    navAufmass: 'Aufmaß',
    navProjects: 'Projekti',
    navBonus: 'Bonus',
    navUsers: 'Korisnici',
    navMobileUsers: 'Mobilni korisnici',
    navInventory: 'Magacin',
    navFleet: 'Vozni park',
    navMyWork: 'Zadaci',
    navPeople: 'Ljudi',
    navExecution: 'Izvođenje',
    navBilling: 'Naplata',
    navCrews: 'Kolone',
    navChangelog: 'Dnevnik izmjena',
    navGroupWork: 'Rad',
    navGroupOps: 'Operacije',
    navGroupAdmin: 'Admin',
    menu: 'Meni',
    pages: 'Stranice',
    openMenu: 'Otvori meni',
    closeMenu: 'Zatvori meni',
    signOut: 'Odjava',
    language: 'Jezik',
    filters: 'Filteri',
    filtersActive: { one: 'Filteri, {count} aktivan', few: 'Filteri, {count} aktivna', other: 'Filteri, {count} aktivnih' },
    reset: 'Poništi',
    close: 'Zatvori',
    closeNamed: 'Zatvori: {name}',
    showResults: 'Prikaži rezultate',
    search: 'Pretraga',
    loading: 'Učitavanje…',
    retry: 'Pokušaj ponovo',
    save: 'Sačuvaj',
    cancel: 'Otkaži',
    edit: 'Izmijeni',
    delete: 'Obriši',
    all: 'Sve',
    none: 'Nijedan',
    yes: 'Da',
    no: 'Ne',
    previousPage: 'Prethodna strana',
    nextPage: 'Sljedeća strana',
    rowsPerPage: 'Redova po strani',
    rows: 'Redovi',
    noData: 'Nema podataka.',
    couldNotLoad: 'Nije moguće učitati: {what}.',
    readOnly: 'Samo za čitanje',
    metres: 'Metri',
    hours: 'Sati',
    crewDays: { one: '{count} dan kolone', few: '{count} dana kolona', other: '{count} dana kolona' },
    crews: { one: '{count} kolona', few: '{count} kolone', other: '{count} kolona' },
  },
  bs: {
    appName: 'Aufmass',
    madeBy: 'Izradili SK Media d.o.o. & XPRO d.o.o. Slovenia',
    noProject: '(bez projekta)',
    navDashboard: 'Pregled',
    navRtk: 'RTK',
    navAufmass: 'Aufmaß',
    navProjects: 'Projekti',
    navBonus: 'Bonus',
    navUsers: 'Korisnici',
    navMobileUsers: 'Mobilni korisnici',
    navInventory: 'Magacin',
    navFleet: 'Vozni park',
    navMyWork: 'Zadaci',
    navPeople: 'Ljudi',
    navExecution: 'Izvođenje',
    navBilling: 'Naplata',
    navCrews: 'Kolone',
    navChangelog: 'Dnevnik izmjena',
    navGroupWork: 'Rad',
    navGroupOps: 'Operacije',
    navGroupAdmin: 'Admin',
    menu: 'Meni',
    pages: 'Stranice',
    openMenu: 'Otvori meni',
    closeMenu: 'Zatvori meni',
    signOut: 'Odjava',
    language: 'Jezik',
    filters: 'Filteri',
    filtersActive: { one: 'Filteri, {count} aktivan', few: 'Filteri, {count} aktivna', other: 'Filteri, {count} aktivnih' },
    reset: 'Poništi',
    close: 'Zatvori',
    closeNamed: 'Zatvori: {name}',
    showResults: 'Prikaži rezultate',
    search: 'Pretraga',
    loading: 'Učitavanje…',
    retry: 'Pokušaj ponovo',
    save: 'Spasi',
    cancel: 'Otkaži',
    edit: 'Uredi',
    delete: 'Izbriši',
    all: 'Sve',
    none: 'Nijedan',
    yes: 'Da',
    no: 'Ne',
    previousPage: 'Prethodna stranica',
    nextPage: 'Sljedeća stranica',
    rowsPerPage: 'Redova po stranici',
    rows: 'Redovi',
    noData: 'Nema podataka.',
    couldNotLoad: 'Nije moguće učitati: {what}.',
    readOnly: 'Samo za čitanje',
    metres: 'Metri',
    hours: 'Sati',
    crewDays: { one: '{count} dan kolone', few: '{count} dana kolona', other: '{count} dana kolona' },
    crews: { one: '{count} kolona', few: '{count} kolone', other: '{count} kolona' },
  },
  sr: {
    appName: 'Aufmass',
    madeBy: 'Izradili SK Media d.o.o. & XPRO d.o.o. Slovenia',
    noProject: '(bez projekta)',
    navDashboard: 'Pregled',
    navRtk: 'RTK',
    navAufmass: 'Aufmaß',
    navProjects: 'Projekti',
    navBonus: 'Bonus',
    navUsers: 'Korisnici',
    navMobileUsers: 'Mobilni korisnici',
    navInventory: 'Magacin',
    navFleet: 'Vozni park',
    navMyWork: 'Zadaci',
    navPeople: 'Ljudi',
    navExecution: 'Izvođenje',
    navBilling: 'Naplata',
    navCrews: 'Kolone',
    navChangelog: 'Dnevnik izmena',
    navGroupWork: 'Rad',
    navGroupOps: 'Operacije',
    navGroupAdmin: 'Admin',
    menu: 'Meni',
    pages: 'Stranice',
    openMenu: 'Otvori meni',
    closeMenu: 'Zatvori meni',
    signOut: 'Odjava',
    language: 'Jezik',
    filters: 'Filteri',
    filtersActive: { one: 'Filteri, {count} aktivan', few: 'Filteri, {count} aktivna', other: 'Filteri, {count} aktivnih' },
    reset: 'Poništi',
    close: 'Zatvori',
    closeNamed: 'Zatvori: {name}',
    showResults: 'Prikaži rezultate',
    search: 'Pretraga',
    loading: 'Učitavanje…',
    retry: 'Pokušaj ponovo',
    save: 'Sačuvaj',
    cancel: 'Otkaži',
    edit: 'Izmeni',
    delete: 'Obriši',
    all: 'Sve',
    none: 'Nijedan',
    yes: 'Da',
    no: 'Ne',
    previousPage: 'Prethodna strana',
    nextPage: 'Sledeća strana',
    rowsPerPage: 'Redova po strani',
    rows: 'Redovi',
    noData: 'Nema podataka.',
    couldNotLoad: 'Nije moguće učitati: {what}.',
    readOnly: 'Samo za čitanje',
    metres: 'Metri',
    hours: 'Sati',
    crewDays: { one: '{count} dan kolone', few: '{count} dana kolona', other: '{count} dana kolona' },
    crews: { one: '{count} kolona', few: '{count} kolone', other: '{count} kolona' },
  },
})
